import type { Draft } from "@reduxjs/toolkit";
import type { Bitmap } from "@brickme/project-core/src/bitmap/types.ts";
import type { BitmapMask } from "@brickme/project-core/src/model/bitmap-mask.ts";
import type {
	FaceBoundingBox,
	Project,
} from "@brickme/project-core/src/model/project.ts";
import type {
	PictureConfiguration,
	BrickedPictureOutput,
} from "@brickme/project-core/src/model/picture.ts";
import type { UnixTimestamp } from "@brickme/project-core/src/model/time.ts";
import type { EncodedImage } from "@brickme/project-core/src/utils/encoded-image.ts";
import type { BitmapPosteriseImageResult } from "@brickme/project-core/src/model/posterise.ts";
import type { OnceOffLoad } from "~/utils/loading.ts";

type CreatorOtherVersion = {
	readonly id: string;
	readonly picture: PictureConfiguration;
};

type CreatorProjectSource = {
	readonly smallestEncodedImage: EncodedImage;
	readonly bitmap: Bitmap;
	readonly originalEncodedImage: EncodedImage | undefined;
};

type CreatorProject = Omit<Project, "otherVersions"> & {
	readonly sourceImage: CreatorProjectSource;
	readonly faceBoundingBoxes:
		| readonly Pick<FaceBoundingBox, "x" | "y" | "width" | "height">[]
		| undefined;
	readonly facesMask: BitmapMask | undefined;
	readonly backgroundMask: OnceOffLoad<BitmapMask>;
	readonly enhanceFacesImage: OnceOffLoad<Bitmap>;
	readonly posteriseImage: OnceOffLoad<BitmapPosteriseImageResult>;
	readonly colorisationImage: OnceOffLoad<Bitmap>;
	readonly otherVersions: readonly CreatorOtherVersion[];
};

function creatorProjectToCoreProject(project: CreatorProject): Project {
	return {
		...project,
		otherVersions: project.otherVersions.map(({ picture }) => picture),
	};
}

type OpenProject = {
	readonly build: BrickedPictureOutput;
	readonly builtCurrentPictureUpdatedAt: UnixTimestamp;
	readonly project: CreatorProject;
};

type State = {
	readonly openProject?: OpenProject;
};

function requireOpenProject(state: Draft<State>): Draft<OpenProject> {
	const { openProject } = state;
	if (!openProject) {
		throw new Error("No open project");
	}
	return openProject;
}

export { requireOpenProject, creatorProjectToCoreProject };
export type {
	OpenProject,
	CreatorProject,
	CreatorProjectSource,
	CreatorOtherVersion,
	State,
};
